import React, { useState, useCallback, useMemo, useEffect } from 'react';
import AudioTrackCard from './AudioTrackCard';
import { MUSIC_LIBRARY, MusicTrack } from '../constants';
import { getRecentlyUsed } from './audioOverlayLibraryUtils';
import { useAudioPreview } from './useAudioPreview';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import Button from '@/components/atoms/Button/Button';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import Icon from '@/components/atoms/Icon';

type SelectionSource = 'library' | 'upload' | null;

interface AudioOverlayLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: { type: 'library'; musicId: string } | { type: 'upload'; file: File }) => Promise<void>;
  currentMusic?: MusicTrack | null;
}

/**
 * AudioOverlayLibraryModal - Modal for selecting music from library or uploading custom audio.
 *
 * Features:
 * - Upload section with drag & drop and file picker (auto-confirms on upload, parent closes modal right before toast)
 * - "Last action wins" precedence - switching sources deselects previous selection
 */
const AudioOverlayLibraryModal: React.FC<AudioOverlayLibraryModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentMusic = null
}) => {
  const [selectedMusicId, setSelectedMusicId] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [lastSelectionSource, setLastSelectionSource] = useState<SelectionSource>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentlyUsedIds, setRecentlyUsedIds] = useState<string[]>([]);

  const { isPlaying, playTrack, stopTrack } = useAudioPreview();

  const isCustomUpload = useMemo(() => {
    return currentMusic !== null && currentMusic.id.startsWith('custom-');
  }, [currentMusic]);

  const customUploadTrack = useMemo(() => {
    return isCustomUpload ? currentMusic : null;
  }, [isCustomUpload, currentMusic]);

  const recentlyUsedTracks = useMemo(() => {
    const libraryTracks = recentlyUsedIds.map(id => MUSIC_LIBRARY[id]).filter(Boolean);

    if (customUploadTrack) {
      return [customUploadTrack, ...libraryTracks];
    }

    return libraryTracks;
  }, [recentlyUsedIds, customUploadTrack]);

  useEffect(() => {
    if (isOpen) {
      setRecentlyUsedIds(getRecentlyUsed());
      if (currentMusic) {
        setSelectedMusicId(currentMusic.id);
        setLastSelectionSource(isCustomUpload ? 'upload' : 'library');
        setUploadedFile(null);
      } else {
        setSelectedMusicId(null);
        setLastSelectionSource(null);
        setUploadedFile(null);
      }
      setIsSubmitting(false);
    } else {
      setSelectedMusicId(null);
      setUploadedFile(null);
      setLastSelectionSource(null);
      setIsSubmitting(false);
      stopTrack();
    }
  }, [isOpen, currentMusic, isCustomUpload, stopTrack]);

  const handleMusicSelect = useCallback(
    (musicId: string) => {
      if (isSubmitting) {
        return;
      }
      setSelectedMusicId(musicId);
      setUploadedFile(null);
      const isCustom = currentMusic && musicId === currentMusic.id && isCustomUpload;
      setLastSelectionSource(isCustom ? 'upload' : 'library');
    },
    [currentMusic, isCustomUpload, isSubmitting]
  );

  const handlePlayPause = useCallback(
    (trackId: string) => {
      if (currentMusic && trackId === currentMusic.id) {
        playTrack(trackId, currentMusic.url);
        return;
      }
      const track = MUSIC_LIBRARY[trackId];
      if (track) {
        playTrack(trackId, track.url);
      }
    },
    [playTrack, currentMusic]
  );

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      if (files.length > 0 && !isSubmitting) {
        const file = files[0];
        setUploadedFile(file);
        setSelectedMusicId(null);
        setLastSelectionSource('upload');
        stopTrack();
        setIsSubmitting(true);
        try {
          await onConfirm({ type: 'upload', file });
        } catch (error) {
          setIsSubmitting(false);
        }
      }
    },
    [onConfirm, stopTrack, isSubmitting]
  );

  const handleCancel = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    onClose();
  }, [onClose, isSubmitting]);

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (lastSelectionSource === 'library' && selectedMusicId) {
        await onConfirm({ type: 'library', musicId: selectedMusicId });
      } else if (lastSelectionSource === 'upload' && uploadedFile) {
        await onConfirm({ type: 'upload', file: uploadedFile });
      } else if (
        lastSelectionSource === 'upload' &&
        !uploadedFile &&
        currentMusic &&
        selectedMusicId === currentMusic.id &&
        isCustomUpload
      ) {
        setIsSubmitting(false);
        onClose();
      }
    } catch (error) {
      setIsSubmitting(false);
    }
  }, [
    lastSelectionSource,
    selectedMusicId,
    uploadedFile,
    onConfirm,
    currentMusic,
    isCustomUpload,
    onClose,
    isSubmitting
  ]);

  const isConfirmEnabled = useMemo(() => {
    if (lastSelectionSource === null) {
      return false;
    }

    if (lastSelectionSource === 'upload' && currentMusic && selectedMusicId === currentMusic.id && isCustomUpload) {
      return true;
    }

    return selectedMusicId !== null || uploadedFile !== null;
  }, [lastSelectionSource, selectedMusicId, uploadedFile, currentMusic, isCustomUpload]);

  const modalTitle = currentMusic ? 'Replace Music' : 'Add Music';

  return (
    <ContentDialog isOpen={isOpen} setIsOpen={onClose} title={modalTitle} size="large" panelClassNames="max-h-[80vh]">
      <div
        className={`flex max-h-[calc(80vh-120px)] flex-col ${isSubmitting ? 'pointer-events-none' : ''}`}
        data-testid="audio-library-modal"
      >
        <div data-testid="upload-section" className="border-b border-slate-200 px-4 pb-6 pt-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">Upload Custom Audio</h3>
          <div className="relative h-[120px]">
            <FilesInput
              onFilesUpload={handleFileUpload}
              accept=".mp3,.wav,.aac,.ogg,.m4a"
              multiple={false}
              label={uploadedFile ? uploadedFile.name : 'Click to browse, or drag & drop an audio file'}
              variant="default"
              isDisabled={isSubmitting}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-6">
          {recentlyUsedTracks.length > 0 && (
            <div data-testid="recently-used-section" className="mb-6">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Recently Used</h3>
              <div className="grid grid-cols-2 gap-3">
                {recentlyUsedTracks.map(track => {
                  const isCustomTrack = currentMusic && track.id === currentMusic.id && isCustomUpload;
                  const isSelected =
                    (lastSelectionSource === 'library' && selectedMusicId === track.id) ||
                    (lastSelectionSource === 'upload' && isCustomTrack && selectedMusicId === track.id);
                  return (
                    <AudioTrackCard
                      key={track.id}
                      track={track}
                      isSelected={!!isSelected}
                      onClick={handleMusicSelect}
                      isPlaying={isPlaying(track.id)}
                      onPlayPause={handlePlayPause}
                      disabled={isSubmitting}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div data-testid="stock-music-section">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Stock Music</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.values(MUSIC_LIBRARY).map(track => (
                <AudioTrackCard
                  key={track.id}
                  track={track}
                  isSelected={lastSelectionSource === 'library' && selectedMusicId === track.id}
                  onClick={handleMusicSelect}
                  isPlaying={isPlaying(track.id)}
                  onPlayPause={handlePlayPause}
                  disabled={isSubmitting}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 px-4 py-2">
          <Button
            data-testid="cancel-button"
            variation="outline"
            trackingId="cancel-button"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            data-testid="confirm-button"
            variation="filled"
            trackingId="confirm-button"
            onClick={handleConfirm}
            disabled={!isConfirmEnabled || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Icon name="IconLoader2" className="mr-2 size-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </div>
      </div>
    </ContentDialog>
  );
};

export default AudioOverlayLibraryModal;
